import React, { useRef, useState } from "react";
import { Modal, Button, Typography } from "antd";
import {
  UploadOutlined,
  ReloadOutlined,
  CameraOutlined,
  ScissorOutlined,
} from "@ant-design/icons";
import multiavatar from "@multiavatar/multiavatar";
import { useClipboardPaste } from "../../Components/DragDropUpload";
import ImageEditorModal from "../../Components/ImageEditorModal";
import "./perfil.css";

const { Text } = Typography;

/**
 * Elección de avatar que devuelve el picker al confirmar. Es explícita (no se
 * infiere de estado del hook) para evitar la condición de carrera que tenía el
 * `submitProfile` viejo, donde `avatarType` podía estar desfasado y terminar
 * sobrescribiendo una foto subida con una semilla. Ver `docs/modulo-perfil.md`.
 */
export type AvatarChoice =
  | { type: "upload"; file: File }
  | { type: "seed"; seed: string };

export interface AvatarPickerProps {
  /** Semilla actual (Multiavatar) para la vista previa inicial. */
  initialSeed: string;
  /** URL de la imagen ya subida, si el usuario tiene una. */
  initialUrl?: string;
  onClose: () => void;
  /** Persiste la elección. Solo se llama si el usuario cambió algo. */
  onConfirm: (choice: AvatarChoice) => void | Promise<void>;
  /** Muestra loading en el botón "Guardar" mientras persiste. */
  saving?: boolean;
}

/** Convierte un data URL (base64) en un File para subir por multipart. */
const dataUrlToFile = (dataUrl: string, baseName: string): File => {
  const [meta, b64] = dataUrl.split(",");
  const mime = /:(.*?);/.exec(meta)?.[1] || "image/png";
  const ext = (mime.split("/")[1] || "png").replace("jpeg", "jpg");
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new File([arr], `${baseName}.${ext}`, { type: mime });
};

/** Lee un File como data URL (para pasarlo al editor Filerobot). */
const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const AvatarPickerModal: React.FC<AvatarPickerProps> = ({
  initialSeed,
  initialUrl,
  onClose,
  onConfirm,
  saving = false,
}) => {
  const [mode, setMode] = useState<"seed" | "upload">(initialUrl ? "upload" : "seed");
  const [seed, setSeed] = useState(initialSeed);
  const [preview, setPreview] = useState<string | null>(initialUrl ?? null);
  const [file, setFile] = useState<File | null>(null);
  const [touched, setTouched] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Editor de imagen (recortar / girar / zoom). Al elegir una imagen se abre
  // directo en modo "crop". `editorSeq` fuerza re-montaje limpio en cada
  // apertura (ver ImageEditorModal). `editorImage` es el data URL a editar.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorImage, setEditorImage] = useState<string | null>(null);
  const [editorSeq, setEditorSeq] = useState(0);

  const openEditor = (dataUrl: string) => {
    setEditorImage(dataUrl);
    setEditorSeq((s) => s + 1);
    setEditorOpen(true);
  };

  // Cualquier imagen entrante (clic, arrastrar, pegar) abre el editor para que
  // el usuario la acomode (recortar, acercar/alejar, girar) antes de usarla.
  const acceptFile = async (f: File) => {
    if (!f.type.startsWith("image/")) return;
    const dataUrl = await fileToDataUrl(f);
    openEditor(dataUrl);
  };

  // El resultado editado (data URL ya comprimido por ImageEditorModal) pasa a
  // ser la vista previa y el File a subir.
  const handleEditedImage = async (editedDataUrl: string) => {
    setFile(dataUrlToFile(editedDataUrl, "avatar"));
    setPreview(editedDataUrl);
    setMode("upload");
    setTouched(true);
    setEditorOpen(false);
  };

  // Ctrl+V mientras el modal del picker está montado y el editor NO está
  // abierto (regla de oro de uploads: clic + arrastrar + pegar).
  useClipboardPaste({
    active: !editorOpen,
    accept: "image/*",
    onFile: acceptFile,
  });

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void acceptFile(f);
    // Permite volver a elegir el MISMO archivo (onChange no dispara si el
    // value no cambió).
    e.target.value = "";
  };

  const randomize = () => {
    setSeed(Math.random().toString(36).substring(2));
    setFile(null);
    setPreview(null);
    setMode("seed");
    setTouched(true);
  };

  const handleConfirm = async () => {
    // Si el usuario no cambió nada, "Guardar" es solo cerrar — así no
    // reescribimos el avatar existente ni disparamos una request inútil.
    if (!touched) {
      onClose();
      return;
    }
    if (mode === "upload" && file) {
      await onConfirm({ type: "upload", file });
    } else {
      await onConfirm({ type: "seed", seed });
    }
  };

  return (
    <>
      <Modal
        open
        centered
        width={440}
        title="Selecciona tu avatar"
        onCancel={saving ? undefined : onClose}
        maskClosable={!saving}
        footer={[
          <Button key="cancel" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>,
          <Button key="ok" type="primary" loading={saving} onClick={handleConfirm}>
            Guardar
          </Button>,
        ]}
      >
        <div className="avatar-picker-body">
          <div
            className={`avatar-picker-preview${dragActive ? " is-drag" : ""}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              const f = e.dataTransfer.files?.[0];
              if (f) void acceptFile(f);
            }}
            title="Haz clic o arrastra una imagen"
          >
            {preview ? (
              <img src={preview} alt="Vista previa del avatar" className="avatar-img" />
            ) : (
              <div
                className="avatar-svg"
                dangerouslySetInnerHTML={{ __html: multiavatar(seed) }}
              />
            )}
            <span className="avatar-picker-cam" aria-hidden>
              <CameraOutlined />
            </span>
          </div>

          <Text type="secondary" className="avatar-picker-hint">
            Haz clic en el avatar o arrastra una imagen. También puedes pegarla
            con Ctrl+V. Al subirla podrás recortarla, girarla y acercarla.
          </Text>

          <div className="avatar-picker-actions">
            <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
              Subir imagen
            </Button>
            {mode === "upload" && preview && (
              <Button
                icon={<ScissorOutlined />}
                onClick={() => openEditor(preview)}
              >
                Recortar / ajustar
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={randomize}>
              Avatar aleatorio
            </Button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png, image/jpeg, image/webp"
            style={{ display: "none" }}
            onChange={handleFileInput}
          />
        </div>
      </Modal>

      {/* Editor Filerobot (lazy) — recortar / girar / voltear / zoom. Se abre
          en modo "crop", enfocado para foto de perfil. `key` fuerza montaje
          limpio con la imagen nueva en cada apertura. */}
      <ImageEditorModal
        key={editorSeq}
        open={editorOpen}
        initialImage={editorImage}
        editorMode="crop"
        onClose={() => setEditorOpen(false)}
        onSave={handleEditedImage}
      />
    </>
  );
};
